import {
  Button,
  Card,
  TextField,
  TimePicker,
  Typography,
  Link
} from "@cambridgeassessment/cambridge-ui";
import { EmbargoNotification } from "./EmbargoNotification";
import { useState, useEffect, useReducer } from "react";
import "./ManageResultsDelay.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Customdropdown } from "../../common/Dropdown";
import { SeriesEmbargoModal } from "../../common/Modal";
import { SectionSpinner } from "../../common/Spinner";
import {
  getRecentExamSeries,
  reportMakerQual,
  transformResponseToBST,
  exportToExcel,
  convertTo12HourFormat
} from "../../constants";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { embargoReducer, initialState, validateFields } from "./reducer";
import { useSelector , useDispatch } from "react-redux";
import moment from "moment";
import "moment/locale/en-gb";
import {
  getSeriesResultEmbargoDate,
  updateSeriesResultEmbargoDates,
  downloadEmbarogList
} from "../../services/muleSoftServices";
import { setAlert } from "../../redux/slice/GlobalMessage";
moment.locale("en-gb");

const ManageResultsDelay = () => {
  const [embargoState, dispatch] = useReducer(embargoReducer, initialState);
  const reportMakerSeries = getRecentExamSeries();
  const [selectedSeries, setSelectedSeries] = useState(
    reportMakerSeries[0].value
  );
  const [resultRelease, setResultRelease] = useState({
    ci_result_release_date : null,
    rm_result_release_date : null
  });

  const [seriesModal, setSeriesModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formValidation, setFormValidation] = useState(null);
  const [emptyDataOrError, setEmptyDataOrError] = useState(false);
  const [download,setDownload] = useState(false);

  const { roleFromSAP, totalLoggedInData: { access_token }, orgs = {} } = useSelector(
    state => state.userInfo || {}
  );
  const reduxDispatch = useDispatch();
  const {
    seriesEmbargoDate,
    seriesTimePicker,
    centersEmbargo,
    qualEmbargo,
    centerRangeEmbargo,
  } = embargoState;

  useEffect(() => {
    if (selectedSeries) {
      const loadData = async () => {
        setLoading(true);
        setEmptyDataOrError(false);
        try {
          const response = await getSeriesResultEmbargoDate({
            series: selectedSeries,
            token: access_token,
            roleName: roleFromSAP,
            bpId : orgs[0]?.bpid
          });
          if (response?.data?.result_release_date) {
            const bstResponseCI = transformResponseToBST(
              response?.data?.result_release_date
            );
            const bstResponseRM = transformResponseToBST(
              response?.data?.result_release_date_rm
            );
            const formattedCI = `${bstResponseCI?.date} ${bstResponseCI?.time} ${bstResponseCI?.format}`;
            const formattedRM = `${bstResponseRM?.date} ${bstResponseRM?.time} ${bstResponseRM?.format}`;

            setResultRelease({
              ci_result_release_date: formattedCI,
              rm_result_release_date: formattedRM,
            });

            dispatch({
              type: "SET_SERIES_DATA",
              payload: {
                ...bstResponseRM,
                initialLoad: true,
              },
            });
          } else {
            setEmptyDataOrError(true);
            setLoading(false);
          }
        } catch (error) {
          console.log(`Exception while processing: ${error}`);
        } finally {
          setLoading(false);
        }
      };
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeries,access_token]);

  const validateAndSubmitEmbargo = async (submissionType) => {
    const { errors = [], isValid } = validateFields(
      submissionType,
      embargoState,
      resultRelease?.rm_result_release_date
    );
  
    if (!isValid && errors.length) {
      setFormValidation(errors.join(", "));
      return;
    }

    try {
      setDownload(true);
      const response = await updateSeriesResultEmbargoDates({
        ...embargoState,
        series: selectedSeries,
        roleName: roleFromSAP,
        token: access_token,
        submissionType: submissionType,
        bpId: orgs[0]?.bpid,
      });
      if(response.error){
        reduxDispatch(setAlert({message:`${response?.message} error while processing the data`,status:"error"}));
      }
      if(response?.data?.status === "error"){
        reduxDispatch(setAlert({message:`${response.data.message}`,status:"error"}));
      }
      if(response?.data?.status === "success"){
          reduxDispatch(setAlert({message:`${response.data.message}`,status:"success"}));
          dispatch({
            type: "RESET_ALL",
          });
          const bstResponseCI = transformResponseToBST(
            response?.data?.result_release_date
          );
          const bstResponseRM = transformResponseToBST(
            response?.data?.result_release_date_rm
          );
          const formattedCI = `${bstResponseCI?.date} ${bstResponseCI?.time} ${bstResponseCI?.format}`;
          const formattedRM = `${bstResponseRM?.date} ${bstResponseRM?.time} ${bstResponseRM?.format}`;

          setResultRelease({
            ci_result_release_date: formattedCI,
            rm_result_release_date: formattedRM,
          });

          dispatch({
            type: "SET_SERIES_DATA",
            payload: {
              ...bstResponseRM,
              initialLoad: true,
            },
          });
      }
    } catch (error) {
      console.error("Error submitting embargo data:", error);
    }finally {
      setDownload(false);
    }
  };
  
  const downloadEmbargoExcel = async () => {
    setDownload(true);
    try {
      const response = await downloadEmbarogList({
        series: selectedSeries,
        token: access_token,
        roleName: roleFromSAP,
        bpId: orgs[0]?.bpid,
      });
      if (Array.isArray(response?.data)) {
        exportToExcel(response.data, "download-exception.xlsx");
      } else {
        console.warn("Unexpected response format:", response?.data);
      }
    } catch (error) {
      console.error("Exception while processing:", error);
    } finally {
      setDownload(false);
    }
  }

  useEffect(() => {
    if (formValidation) {
      const timer = setTimeout(() => {
        setFormValidation("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [formValidation]);

  return (
    <>
      {loading ? (
        <SectionSpinner />
      ) : (
        <div className="embarogManagement">
          { download && <SectionSpinner /> }
          <div className="row mb-4">
            <div className="col-lg-3 col-md-12 col-sm-12 mt-2">
              <Customdropdown
                selectedValue={selectedSeries}
                class=""
                label="Please select a series"
                handleChange={e => setSelectedSeries(e.target.value)}
                placeholder="Please select..."
                datas={reportMakerSeries}
              />
            </div>
          </div>
          {emptyDataOrError ? (
            <EmbargoNotification />
          ) : (
            <div>
              <Card className="mb-4" style={{ padding: "10px 24px" }}>
                <div className="row">
                  <div className="col-lg-8 col-md-12 col-sm-12">
                    <h3 className="card-header text-start mb-2">
                      CI Direct Series result release date : {convertTo12HourFormat(resultRelease?.ci_result_release_date)}
                    </h3>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-8 col-md-12 col-sm-12">
                    <h3 className="card-header text-start mb-2">
                      Report Maker Series result release date : {convertTo12HourFormat(resultRelease?.rm_result_release_date)}
                    </h3>
                  </div>
                </div>
              </Card>
              <Card className="mb-4" style={{ padding: "10px 24px" }}>
                <div className="row">
                  <div className="col-lg-10 col-md-12 col-sm-12">
                    <h3 className="card-header text-start mb-2">
                      Add a series delay to {selectedSeries} results on
                      Report Maker
                    </h3>
                    <p className="mb-4 text-start card-sub-head">
                      Please note, this date cannot be before the series results
                      release date shown above
                    </p>
                  </div>
                  <div
                    className={`col-lg-2 col-md-12 col-sm-12 ${
                      window.innerWidth < 821 ? "text-start mb-4" : "text-end"
                    }`}>
                    <Button
                      variant="outlined"
                      disabled={!selectedSeries}
                      onClick={e => setSeriesModal(!seriesModal)}>
                      Select Date
                    </Button>
                  </div>
                </div>
              </Card>
              <Card className="mb-4" style={{ padding: "10px 24px" }}>
                <div className="row">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h3 className="card-header text-start mb-0">
                        Add a new exception for this session on Report Maker
                      </h3>
                      <Typography>
                        <Link
                          className="downlaod_excel"
                          sx={{
                            fontSize: "0.9rem",
                            cursor: "pointer",
                            color: "#0076bd",
                          }}
                          
                          onClick={downloadEmbargoExcel}>
                          Download Exceptions
                        </Link>
                      </Typography>
                    </div>
                  </div>
                  <div className="col-lg-8 col-md-12 col-sm-12">
                    <p className="mb-4 text-start card-sub-head">
                      Please enter a list of centers by center number, separated
                      by commas (for example: US001,US005,US100,US200)
                    </p>
                    <p className="mb-4 text-start card-sub-head">
                      Please note, this date cannot be before the series results
                      release date shown above
                    </p>
                  </div>
                </div>
                <p style={{ color: "red" }}>{formValidation}</p>
                <div className="mb-4 row">
                  <div className="col-lg-3 customInputField">
                    <TextField
                      label="Centers"
                      placeholder="Enter Centers"
                      value={centersEmbargo.centers}
                      autoComplete="off"
                      onChange={e => {
                        const noSpaces = e.target.value.replace(/\s+/g, '').toUpperCase();
                        dispatch({
                          type: "UPDATE_FIELD",
                          payload: {
                            section: "centersEmbargo",
                            field: "centers",
                            value: noSpaces,
                          },
                        });
                      }}
                    />
                  </div>
                  <div className="col-lg-3">
                    <Customdropdown
                      key={centersEmbargo.qual}
                      selectedValue={centersEmbargo.qual}
                      class=""
                      label="Select Qualification"
                      handleChange={e =>
                        dispatch({
                          type: "UPDATE_FIELD",
                          payload: {
                            section: "centersEmbargo",
                            field: "qual",
                            value: e.target.value,
                          },
                        })
                      }
                      placeholder="Please select..."
                      datas={reportMakerQual}
                    />
                  </div>
                  <div className="col-lg-2">
                    <div className="customDatePicker">
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          label="Select Date"
                          value={centersEmbargo.date}
                          onChange={newDate =>
                            dispatch({
                              type: "UPDATE_FIELD",
                              payload: {
                                section: "centersEmbargo",
                                field: "date",
                                value: newDate
                                  ? newDate.format("DD-MM-YYYY")
                                  : "",
                              },
                            })
                          }
                          inputFormat="DD-MM-YYYY"
                          renderInput={params => (
                            <TextField
                              {...params}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div className="col-lg-2">
                    <div className="customTimePicker">
                      <TimePicker
                        id="timepicker_centers"
                        label="Time (UK)"
                        value={centersEmbargo.time}
                        onChange={value =>
                          dispatch({
                            type: "UPDATE_FIELD",
                            payload: {
                              section: "centersEmbargo",
                              field: "time",
                              value: value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="col-lg-1 offset-lg-1">
                    <Button
                      className="btnQualification"
                      variant="outlined"
                      onClick={e => validateAndSubmitEmbargo("centersEmbargo")}
                      disabled={Object.values(centersEmbargo).some(
                        value => value === "" || value === null
                      )}>
                      Add
                    </Button>
                  </div>
                </div>

                <div className="mb-4 row">
                  <div className="col-lg-6">
                    <Customdropdown
                      key={qualEmbargo.qual}
                      selectedValue={qualEmbargo.qual}
                      class=""
                      label="Select Qualification"
                      handleChange={e =>
                        dispatch({
                          type: "UPDATE_FIELD",
                          payload: {
                            section: "qualEmbargo",
                            field: "qual",
                            value: e.target.value,
                          },
                        })
                      }
                      placeholder="Please select..."
                      datas={reportMakerQual}
                    />
                  </div>
                  <div className="col-lg-2">
                    <div className="customDatePicker">
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          label="Select Date"
                          value={qualEmbargo.date}
                          onChange={newDate =>
                            dispatch({
                              type: "UPDATE_FIELD",
                              payload: {
                                section: "qualEmbargo",
                                field: "date",
                                value: newDate
                                  ? newDate.format("DD-MM-YYYY")
                                  : "",
                              },
                            })
                          }
                          inputFormat="DD-MM-YYYY"
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder="DD-MM-YYYY"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div className="col-lg-2">
                    <div className="customTimePicker">
                      <TimePicker
                        id="timepicker_qual"
                        label="Time (UK)"
                        value={qualEmbargo.time}
                        onChange={value =>
                          dispatch({
                            type: "UPDATE_FIELD",
                            payload: {
                              section: "qualEmbargo",
                              field: "time",
                              value: value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="col-lg-1 offset-lg-1">
                    <Button
                      className="btnQualification"
                      variant="outlined"
                      onClick={e => validateAndSubmitEmbargo("qualEmbargo")}
                      disabled={Object.values(qualEmbargo).some(
                        value => value === "" || value === null
                      )}>
                      Add
                    </Button>
                  </div>
                </div>

                <div className="mb-4 row">
                  <div className="col-lg-2">
                    <TextField
                      label="By Center Range"
                      placeholder="From"
                      value={centerRangeEmbargo.from}
                      autoComplete="off"
                      onChange={e => {
                        const input = e.target.value.replace(/\s+/g, '').toUpperCase();
                        if (input.length <= 5) {
                          dispatch({
                            type: "UPDATE_FIELD",
                            payload: {
                              section: "centerRangeEmbargo",
                              field: "from",
                              value: input,
                            },
                          });
                        }
                      }}
                    />
                  </div>
                  <div className="col-lg-2">
                    <TextField
                      label="To"
                      placeholder="To"
                      autoComplete="off"
                      value={centerRangeEmbargo.to}
                      onChange={e => {
                        const input = e.target.value.replace(/\s+/g, '').toUpperCase();
                        if (input.length <= 5) {
                          dispatch({
                            type: "UPDATE_FIELD",
                            payload: {
                              section: "centerRangeEmbargo",
                              field: "to",
                              value: input,
                            },
                          });
                        }
                      }}
                    />
                  </div>
                  <div className="col-lg-2">
                    <Customdropdown
                      key={centerRangeEmbargo.qual}
                      selectedValue={centerRangeEmbargo.qual}
                      class=""
                      label="Select Qual"
                      handleChange={e =>
                        dispatch({
                          type: "UPDATE_FIELD",
                          payload: {
                            section: "centerRangeEmbargo",
                            field: "qual",
                            value: e.target.value,
                          },
                        })
                      }
                      placeholder="Please select..."
                      datas={reportMakerQual}
                    />
                  </div>
                  <div className="col-lg-2">
                    <div className="customDatePicker">
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          label="Select Date"
                          value={centerRangeEmbargo.date}
                          onChange={newDate =>
                            dispatch({
                              type: "UPDATE_FIELD",
                              payload: {
                                section: "centerRangeEmbargo",
                                field: "date",
                                value: newDate
                                  ? newDate.format("DD-MM-YYYY")
                                  : "",
                              },
                            })
                          }
                          inputFormat="DD-MM-YYYY"
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder="DD-MM-YYYY"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div className="col-lg-2">
                    <div className="customTimePicker">
                      <TimePicker
                        id="timepicker_range"
                        label="Time (UK)"
                        value={centerRangeEmbargo.time}
                        onChange={value =>
                          dispatch({
                            type: "UPDATE_FIELD",
                            payload: {
                              section: "centerRangeEmbargo",
                              field: "time",
                              value: value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="col-lg-1 offset-lg-1">
                    <Button
                      className="btnQualification"
                      variant="outlined"
                      onClick={e =>
                        validateAndSubmitEmbargo("centerRangeEmbargo")
                      }
                      disabled={Object.values(centerRangeEmbargo).some(
                        value => value === "" || value === null
                      )}>
                      Add
                    </Button>
                  </div>
                </div>
              </Card>
              <SeriesEmbargoModal
                open={seriesModal}
                close={e => setSeriesModal(!seriesModal)}
                seriesEmbargoDate={seriesEmbargoDate}
                seriesTimePicker={seriesTimePicker}
                dispatch={dispatch}
                resultRelease={resultRelease?.ci_result_release_date}
                setResultRelease={setResultRelease}
                selectedSeries={selectedSeries}
                embargoState={embargoState}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};
export default ManageResultsDelay;